import React, {useEffect} from 'react'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faEnvelope, faUnlockAlt} from '@fortawesome/free-solid-svg-icons';
import {Button, Card, Col, Container, Form, InputGroup, Row} from '@themesberg/react-bootstrap';
import {Link, useHistory} from 'react-router-dom';
import queryString from 'query-string';

import {Routes} from '../../routes';
import API from '../../services/API';
import Utils from '../../services/Utils';
import {ACCESS_TOKEN, EMAIL, NAME, PHONE} from '../../services/constants';
import {FormattedMessage} from 'react-intl';
import BrandLockup from '../../components/BrandLockup';
import {replayAppLoading} from '../../components/Preloader';

// Pages légales du site vitrine (URLs localisées : /{locale}/{slug}/).
const LEGAL_SLUGS = {
  fr: {terms: 'conditions-generales-de-vente', privacy: 'politique-de-confidentialite'},
  en: {terms: 'terms-and-conditions', privacy: 'privacy-policy'},
  es: {terms: 'condiciones-generales-de-venta', privacy: 'politica-de-privacidad'},
};

export default (props) => {
  Utils.setBackground(true);

  const {intl} = props;
  const history = useHistory();
  // Liens légaux dans la langue de l'app (repli sur le français si la locale n'est pas gérée).
  const legalLocale = LEGAL_SLUGS[(intl.locale || 'fr').slice(0, 2)] ? (intl.locale || 'fr').slice(0, 2) : 'fr';
  const legalUrl = (key) => `https://clickchauffeur.com/${legalLocale}/${LEGAL_SLUGS[legalLocale][key]}/`;
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [rememberMe, setRememberMe] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  useEffect(() => {
    Utils.checkAccessTokenForLogin(history);
  });

  useEffect(() => {
    const params = queryString.parse(props.location.search);

    // Confirmation d'email (inscription gratuite) : retour du lien /auth/verify-email.
    const emailVerified = params.emailVerified;
    if (emailVerified === 'success') {
      Utils.notifySuccess(intl.formatMessage({id: 'OK'}), intl.formatMessage({id: 'EMAIL_VERIFIED_SUCCESS'}));
    } else if (emailVerified === 'expired') {
      Utils.notifyWarning(intl.formatMessage({id: 'ERROR'}), intl.formatMessage({id: 'VERIFY_LINK_INVALID'}));
    }

    const emailChange = params.emailChange;
    if (!emailChange) {
      return;
    }
    switch (emailChange) {
      case 'success':
        Utils.notifySuccess(intl.formatMessage({id: 'OK'}), intl.formatMessage({id: 'EMAIL_CHANGE_SUCCESS'}));
        break;
      case 'expired':
        Utils.notifyWarning(intl.formatMessage({id: 'ERROR'}), intl.formatMessage({id: 'EMAIL_CHANGE_EXPIRED'}));
        break;
      case 'alreadyUsed':
        Utils.notifyWarning(intl.formatMessage({id: 'ERROR'}), intl.formatMessage({id: 'EMAIL_CHANGE_ALREADY_USED'}));
        break;
      default:
        Utils.notifyError(intl.formatMessage({id: 'ERROR'}), intl.formatMessage({id: 'EMAIL_CHANGE_FAILED'}));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    API.postSignIn(email, password, rememberMe).then((response => {
      setLoading(false);
      if (response && response.data.success === true) {
        localStorage.setItem(ACCESS_TOKEN, response.data.data.accessToken);
        localStorage.setItem(EMAIL, response.data.data.email);
        localStorage.setItem(NAME, response.data.data.name);
        if (response.data.data.phone) {
          localStorage.setItem(PHONE, response.data.data.phone);
        } else {
          localStorage.setItem(PHONE, '');
        }
        // Écran de chargement le temps que la plateforme se mette en place, plutôt qu'un saut
        // brutal du formulaire au tableau de bord. À déclencher avant la redirection : c'est au
        // montage du wrapper de route que le préchargeur lit l'état de chargement.
        replayAppLoading();
        history.push('/admin/dashboard');
      } else if (response && response.data && response.data.data === 'EMAIL_NOT_VERIFIED') {
        // Compte gratuit dont l'email n'a pas encore été confirmé.
        Utils.notifyWarning(intl.formatMessage({id: 'UNABLE_TO_CONNECT'}), intl.formatMessage({id: 'EMAIL_NOT_VERIFIED_LOGIN'}));
      } else {
        Utils.notifyError(intl.formatMessage({id: 'ERROR'}), intl.formatMessage({id: 'ERROR_OCCURRED'}));
      }
    }))
      .catch(error => {
        setLoading(false);
        if (error.response.status === 401) {
          Utils.notifyWarning(intl.formatMessage({id: 'UNABLE_TO_CONNECT'}), intl.formatMessage({id: 'INCORRECT_LOGIN'}));
        } else {
          Utils.notifyError(intl.formatMessage({id: 'ERROR'}), intl.formatMessage({id: 'ERROR_OCCURRED'}));
        }
      });
  }

  return (
    <main>
      <section className="d-flex align-items-center my-5 mt-lg-5 mb-lg-5">
        <Container>
          <BrandLockup/>
          <Row className="justify-content-center form-bg-image">
            <Col xs={12} className="d-flex align-items-center justify-content-center">
              <div className="bg-white p-4 p-lg-5 w-100 fmxw-500 cc-auth-card">
                <div className="text-center text-md-center mb-4 mt-md-0">
                  <h3 className="mb-0"><FormattedMessage id="LOGIN"/></h3>
                </div>
                <Form className="mt-4" onSubmit={handleSubmit}>
                  <Form.Group id="email" className="mb-4">
                    <Form.Label><FormattedMessage id="EMAIL_ADDRESS"/></Form.Label>
                    <InputGroup>
                      <InputGroup.Text>
                        <FontAwesomeIcon icon={faEnvelope}/>
                      </InputGroup.Text>
                      <Form.Control autoFocus required type="email" placeholder="example@company.com"
                                    onChange={(e) => setEmail(e.target.value)}
                                    value={email}/>
                    </InputGroup>
                  </Form.Group>
                  <Form.Group>
                    <Form.Group id="password" className="mb-4">
                      <Form.Label><FormattedMessage id="PASSWORD"/></Form.Label>
                      <InputGroup>
                        <InputGroup.Text>
                          <FontAwesomeIcon icon={faUnlockAlt}/>
                        </InputGroup.Text>
                        <Form.Control required type="password" placeholder={intl.formatMessage({id: 'PASSWORD'})}
                                      onChange={(e) => setPassword(e.target.value)}
                                      value={password}/>
                      </InputGroup>
                    </Form.Group>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <Form.Check label={intl.formatMessage({id: 'REMEMBER_ME'})} id="rememberMe"
                                  onChange={(e) => setRememberMe(e.target.checked)}
                                  checked={rememberMe}/>
                      <Card.Link style={{marginLeft: '20px'}} as={Link} to={Routes.ForgotPassword.path} className="small text-end"><FormattedMessage id="FORGOT_PASSWORD"/></Card.Link>
                    </div>
                  </Form.Group>
                  <Button variant="primary" type="submit" className="w-100 btn-login" disabled={loading}>
                    {loading && (
                      <i className="spinner-border spinner-border-sm text-light"/>
                    )}
                    {!loading && <FormattedMessage id="SIGN_IN"/>}
                  </Button>
                </Form>

                <div className="cc-auth-sep"><span><FormattedMessage id="OR"/></span></div>

                <Button as={Link} to={Routes.Signup.path} variant="outline-primary" className="w-100 cc-auth-signup">
                  <FormattedMessage id="CREATE_FREE_ACCOUNT"/>
                </Button>

                <div className="cc-auth-legal">
                  <a href={legalUrl('terms')} target="_blank" rel="noopener noreferrer"><FormattedMessage id="CGV"/></a>
                  <span className="cc-auth-legal-dot" aria-hidden="true">·</span>
                  <a href={legalUrl('privacy')} target="_blank" rel="noopener noreferrer"><FormattedMessage id="PRIVACY_POLICY"/></a>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </main>
  );
};
